"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import RetirementStepsTitle from "./RetirementStepsTitle";
import Icon from "@/components/icons/Icons";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { formatLargeINR, getPreRetirementReturnRate } from "@/utils/financialPageFunctions";
import { allSIPCalculation } from "@/utils/allCalculators";
import { PlanningMode } from "@/type/financialPageTypes";

const MIN_INCOME_SALARY = 15_000;
const MIN_INCOME_EXPENSES = 10_000;
const MAX_INCOME = 10_00_00_000;
const MIN_PERCENT = 5;
const MAX_PERCENT = 50;
const MIN_CUSTOM = 10_00_000;
const MAX_CUSTOM = 100_00_00_000;

export default function RetirementIncomeScreen({
  stepTitle,
  expensesStepTitle,
  customStepTitle,
  salaryPercentLabel,
  monthlyIncome,
  onMonthlyIncomeAction,
  salaryPercent,
  onSalaryPercentAction,
  customAmount,
  onCustomAmountAction,
  planningMode,
  sipTenure,
  validateTrigger,
}: {
  stepTitle?: string;
  expensesStepTitle?: string;
  customStepTitle?: string;
  salaryPercentLabel?: string;
  monthlyIncome: string;
  onMonthlyIncomeAction: (v: string) => void;
  salaryPercent: number;
  onSalaryPercentAction: (v: number) => void;
  customAmount: string;
  onCustomAmountAction: (v: string) => void;
  planningMode: PlanningMode;
  sipTenure: number;
  validateTrigger?: number;
}) {
  const showPercent = planningMode === "salary" || planningMode === null;
  const showCustomAmount = planningMode === "custom";
  const showMonthlyExp = planningMode === "expenses";

  const minIncome = showMonthlyExp ? MIN_INCOME_EXPENSES : MIN_INCOME_SALARY;

  const [incomeError, setIncomeError] = useState("");
  const [percentError, setPercentError] = useState("");
  const [customAmountError, setCustomAmountError] = useState("");

  const percentage = Math.min(Math.max(((salaryPercent - MIN_PERCENT) / (MAX_PERCENT - MIN_PERCENT)) * 100, 0), 100);
  const income = parseFloat(monthlyIncome.replace(/,/g, "")) || 0;
  const sipAmount = Math.round((income * salaryPercent) / 100);
  const customAmountParsed = parseFloat(customAmount.replace(/,/g, "")) || 0;

  const monthlyExpAtRetirement = useMemo(() => {
    if (!showMonthlyExp || income <= 0) return 0;
    return Math.round(income * Math.pow(1.03, sipTenure));
  }, [showMonthlyExp, income, sipTenure]);

  const customSipAmount = useMemo(() => {
    if (!showCustomAmount || customAmountParsed <= 0 || sipTenure <= 0) return 0;
    return allSIPCalculation({
      investAmount: 0,
      tenure: sipTenure,
      expectedReturn: getPreRetirementReturnRate(sipTenure),
      targetAmount: customAmountParsed,
      calclId: "targetValueCalc",
      investType: "Monthly",
    }).investment;
  }, [showCustomAmount, customAmountParsed, sipTenure]);

  // --- Validators ---

  const validateIncome = (value: string) => {
    const parsed = parseFloat(value.replace(/,/g, ""));
    if (!value || isNaN(parsed) || parsed < minIncome) {
      setIncomeError(
        showMonthlyExp
          ? `Your monthly expenses should be at least ₹${formatIndianNumber(minIncome)}.`
          : `Please enter a realistic monthly income of at least ₹${formatIndianNumber(minIncome)}.`
      );
      return false;
    }
    if (parsed > MAX_INCOME) {
      setIncomeError(
        showMonthlyExp ? "Monthly expenses cannot exceed ₹10 Crore." : "Monthly income cannot exceed ₹10 Crore."
      );
      return false;
    }
    setIncomeError("");
    return true;
  };

  const validatePercent = (value: number) => {
    if (isNaN(value) || value < MIN_PERCENT) {
      setPercentError(`Salary percentage must be at least ${MIN_PERCENT}%.`);
      return false;
    }
    if (value > MAX_PERCENT) {
      setPercentError(`Salary percentage cannot exceed ${MAX_PERCENT}%.`);
      return false;
    }
    setPercentError("");
    return true;
  };

  const validateCustomAmount = (value: string) => {
    const parsed = parseFloat(value.replace(/,/g, ""));
    if (!value || isNaN(parsed) || parsed < MIN_CUSTOM) {
      setCustomAmountError(`Retirement amount must be at least ₹${formatIndianNumber(MIN_CUSTOM)}.`);
      return false;
    }
    if (parsed > MAX_CUSTOM) {
      setCustomAmountError("Retirement amount cannot exceed ₹100 Crore.");
      return false;
    }
    setCustomAmountError("");
    return true;
  };

  // --- Handlers ---

  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    if (digits === "") {
      setIncomeError("");
      onMonthlyIncomeAction("");
      return;
    }
    const parsed = parseInt(digits, 10);
    if (isNaN(parsed)) return;
    setIncomeError("");
    onMonthlyIncomeAction(Math.min(parsed, MAX_INCOME).toString());
  };

  const handlePercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    if (digits === "") {
      setPercentError("");
      onSalaryPercentAction(0);
      return;
    }
    const parsed = parseInt(digits, 10);
    setPercentError("");
    onSalaryPercentAction(Math.min(MAX_PERCENT, isNaN(parsed) ? 0 : parsed));
  };

  const handlePercentBlur = () => {
    const clamped = Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, isNaN(salaryPercent) ? MIN_PERCENT : salaryPercent));
    onSalaryPercentAction(clamped);
    validatePercent(clamped);
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPercentError("");
    onSalaryPercentAction(Number(e.target.value));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    if (digits === "") {
      setCustomAmountError("");
      onCustomAmountAction("");
      return;
    }
    const parsed = parseInt(digits, 10);
    if (isNaN(parsed)) return;
    setCustomAmountError("");
    onCustomAmountAction(Math.min(parsed, MAX_CUSTOM).toString());
  };

  // --- Validate on external trigger ---

  const prevTrigger = useRef(0);
  const monthlyIncomeRef = useRef(monthlyIncome);
  const salaryPercentRef = useRef(salaryPercent);
  const customAmountRef = useRef(customAmount);
  monthlyIncomeRef.current = monthlyIncome;
  salaryPercentRef.current = salaryPercent;
  customAmountRef.current = customAmount;

  useEffect(() => {
    if (!validateTrigger || validateTrigger === prevTrigger.current) return;
    prevTrigger.current = validateTrigger;
    validateIncome(monthlyIncomeRef.current);
    if (showPercent) validatePercent(salaryPercentRef.current);
    if (showCustomAmount) validateCustomAmount(customAmountRef.current);
  }, [validateTrigger]);

  return (
    <div className="flex flex-col h-full">
      <RetirementStepsTitle
        stepTitle={
          showCustomAmount
            ? customStepTitle || "Enter Your Target Retirement Corpus"
            : showMonthlyExp
              ? expensesStepTitle || "Desired Monthly Expenses at Retirement (Today's Value)"
              : stepTitle || "Enter Your Monthly Income"
        }
      />

      {showCustomAmount ? (
        <>
          <label className="flex items-center gap-3 border border-[#DBDBDB] rounded-2xl text-2.5xl font-bold px-4 py-5 bg-white">
            <span className="text-primaryBlack">₹</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="1,00,00,000"
              value={customAmountParsed > 0 ? formatIndianNumber(customAmountParsed) : ""}
              onChange={handleCustomAmountChange}
              onBlur={() => validateCustomAmount(customAmount)}
              className="max-w-full flex-1 bg-transparent text-primaryBlack placeholder-gray-400 outline-hidden"
            />
          </label>
          {customAmountError && <p className="text-xs text-red-500 mt-2">{customAmountError}</p>}

          <div className="rounded-2xl border border-primaryBlue bg-[#EEF5FF] p-4 text-black/60 text-2xs mt-4">
            <strong className="text-primaryBlue">Note:</strong> This is the total retirement fund you&apos;re aiming to
            build up.
          </div>

          {customSipAmount > 0 && (
            <div className="mt-auto mx-auto w-fit border border-[#C6D3EA] rounded-full px-3 py-2.5 sm:px-7 sm:py-3.5 text-sm sm:text-base font-medium text-[#003D6C96] text-center">
              Your Monthly SIP would be{" "}
              <span className="font-bold text-black">₹{formatIndianNumber(customSipAmount)}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <label className="flex items-center gap-3 border border-[#DBDBDB] rounded-2xl text-2.5xl font-bold px-4 py-5 bg-white">
            <span className="text-primaryBlack">₹</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="75,000"
              value={income > 0 ? formatIndianNumber(income) : ""}
              onChange={handleIncomeChange}
              onBlur={() => validateIncome(monthlyIncome)}
              className="max-w-full flex-1 bg-transparent text-primaryBlack placeholder-gray-400 outline-hidden"
            />
          </label>

          {incomeError && <p className="text-xs text-red-500 mt-2">{incomeError}</p>}

          {showMonthlyExp && (
            <div className="rounded-2xl border border-primaryBlue bg-[#EEF5FF] p-4 text-black/60 text-2xs font-semibold mt-4">
              <strong className="text-primaryBlue">Note:</strong> At{" "}
              <span className="text-primaryBlue">3% inflation</span> over{" "}
              <span className="text-primaryBlue">{sipTenure} years</span>, this becomes{" "}
              <span className="text-primaryBlue font-bold">
                {monthlyExpAtRetirement > 0 ? `₹${formatLargeINR(monthlyExpAtRetirement)}/month` : "—"}
              </span>{" "}
              at retirement
            </div>
          )}

          {showMonthlyExp && (
            <p className="text-2xs text-black/50 font-medium mt-3 text-center">
              <b className="text-primaryBlack/70">Disclaimer:</b> Your retirement corpus is calculated assuming this
              monthly expense continues till the age of 80 years.
            </p>
          )}

          {showPercent && (
            <>
              <div className="border-t border-dashed border-[#8D8D8D] my-8" />

              <div className="flex justify-between items-center gap-2">
                <div className="font-semibold text-left text-sm sm:text-base lg:text-xl text-primaryBlack">
                  {salaryPercentLabel || "What % of Salary you want to invest"}
                </div>

                <label className="max-w-150 xs:max-w-200 relative flex justify-start rounded-xl overflow-hidden py-2.5 px-3.5 font-medium text-xs sm:text-sm lg:text-base transition border text-(--primary) border-[rgb(var(--primary-alpha)/0.2)] bg-transparent focus-within:border-[rgb(var(--primary-alpha)/0.3)] hover:border-[rgb(var(--primary-alpha)/0.3)]">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={salaryPercent}
                    onChange={handlePercentChange}
                    onBlur={handlePercentBlur}
                    className="w-full bg-transparent"
                  />
                  <span className="inline-block absolute right-4 top-1/2 -translate-y-1/2 my-auto ml-auto opacity-50 pointer-events-none shadow-[0px_1px_4px_0px_#00000040_inset] text-(--primary)">
                    %
                  </span>
                </label>
              </div>

              {percentError && <p className="text-xs text-red-500 mt-1">{percentError}</p>}

              <div className="relative h-10">
                <div className="absolute left-0 top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-[#E8E8E8]" />
                <div
                  className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full"
                  style={{
                    width: `${percentage}%`,
                    background: "linear-gradient(90deg, #B1DBFA 0%, #003D6C 100%)",
                  }}
                />
                <div
                  className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0px_2px_1.7px_0px_#00000040]"
                  style={{ left: `${percentage}%` }}
                >
                  <Icon.rangeBarCustomIconV2 className="w-7 h-7 sm:w-9 sm:h-9" />
                </div>
                <input
                  type="range"
                  min={MIN_PERCENT}
                  max={MAX_PERCENT}
                  step={1}
                  value={salaryPercent}
                  onChange={handleRangeChange}
                  className="absolute inset-0 z-2 h-full w-full cursor-pointer opacity-0"
                />
              </div>

              <div className="flex items-center justify-between gap-5 text-gray-400 font-bold">
                <span>{MIN_PERCENT}%</span>
                <span>{MAX_PERCENT}%</span>
              </div>

              {sipAmount > 0 && (
                <div className="mt-auto mx-auto w-fit border border-[#C6D3EA] rounded-full px-7 py-3.5 text-lg font-medium text-[#003D6C96]">
                  Your Monthly SIP would be{" "}
                  <span className="font-bold text-black">₹{formatIndianNumber(sipAmount)}</span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
